/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, memo, useCallback } from 'react';
import CommercialAgentContext from './CommercialAgentContext';
import initialCommercialAgentState from './initialCommercialAgentState';
import commercialAgentReducer from './reducer/commercialAgent.reducer';
import * as action from './reducer/commercialAgent.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { IChildrenProps } from 'interfaces/general.interface';
import {
  IassignAgentsToClientActionProps,
  IcreateCommercialAgentActionProps,
  IdeleteCommercialAgentActionProps,
  IselectAgentActionProps,
  IupdateCommercialAgentActionProps,
} from 'interfaces/commercialAgent.interface';
import useContextClients from 'hooks/contexts/useContextClients';
import useContextCompany from 'hooks/contexts/useContextCompany';

function CommercialAgentProvider(props: IChildrenProps) {
  const [commercialAgentState, dispatch] = useReducer(
    commercialAgentReducer,
    initialCommercialAgentState
  );
  const { company } = useContextCompany();
  const { clientSelected } = useContextClients();
  const { getAccessTokenSilently } = useAuth0();
  const { t: translate } = useTranslation();

  const getCommercialAgents = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token || !company?.id) return false;
    return await action.getCommercialAgentsAction({
      dispatch,
      tenantId: company.id,
      token,
      translate,
    });
  }, [company?.id]);

  const selectAgent = useCallback(async ({ agent }: IselectAgentActionProps) => {
    return await action.selectAgentAction({
      dispatch,
      agent,
    });
  }, []);

  const createCommercialAgent = useCallback(
    async ({ agent }: IcreateCommercialAgentActionProps) => {
      if (!company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.createCommercialAgentAction({
        dispatch,
        agent,
        tenantId: company.id,
        token,
        translate,
      });
    },
    [company?.id]
  );

  const updateCommercialAgent = useCallback(
    async ({ agentId, agent }: IupdateCommercialAgentActionProps) => {
      if (!company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateCommercialAgentAction({
        dispatch,
        tenantId: company.id,
        clientsAliasesAssigned:
          commercialAgentState.agents
            .find((agent) => agent.id === agentId)
            ?.comercial_agent_client_aliases.map((client) => client) ?? [],
        agentId,
        agent,
        token,
        translate,
      });
    },
    [company?.id, commercialAgentState.agents]
  );

  const deleteCommercialAgent = useCallback(
    async ({ agentId }: IdeleteCommercialAgentActionProps) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.deleteCommercialAgentAction({
        dispatch,
        agentId,
        token,
        translate,
      });
    },
    []
  );

  const assignAgentsToClient = useCallback(
    async ({ agentsToAddIdx, agentsOnClient }: IassignAgentsToClientActionProps) => {
      if (!clientSelected?.key || !company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.assignAgentsToClientAction({
        dispatch,
        tenantId: company.id,
        agentsToAddIdx,
        agentsOnClient,
        clientId: clientSelected.key,
        token,
        translate,
      });
    },
    [clientSelected?.key, company?.id]
  );

  const memoProvider = useMemo(
    () => ({
      ...commercialAgentState,
      getCommercialAgents,
      selectAgent,
      createCommercialAgent,
      updateCommercialAgent,
      deleteCommercialAgent,
      assignAgentsToClient,
    }),
    [
      commercialAgentState,
      getCommercialAgents,
      selectAgent,
      createCommercialAgent,
      updateCommercialAgent,
      deleteCommercialAgent,
      assignAgentsToClient,
    ]
  );

  return (
    <CommercialAgentContext.Provider value={memoProvider}>
      {props.children}
    </CommercialAgentContext.Provider>
  );
}

export default memo(CommercialAgentProvider);
